// src/components/SectionDialogs.js
//
// BOB, 2026-10-25, two instructions one after the other:
//   1. "each section can be presented as popups instead of having to scroll like mad - personal
//      information, employer information, union information"
//   2. "Creating this way forces the claimant to acknowledge their data remains accurate and constant
//      because the claimant must acknowledge their information. Click accept, No change, Edit... and
//      then... I want to treat the rest of the claim form to be handled in the same manner, grouping
//      it intelligently and accordingly"
//
// So: one card per section of the claim - the three identity sections and the five the form itself
// already uses (what happened, the rule, who else was involved, what you are asking for, exhibits) -
// and each card opens alone. A section OPENS READ-ONLY: he sees what the claim will say, and answers
// it. "Correct - no change" or "Edit". Either answer is recorded with the moment he gave it; an edit
// to a value he had already confirmed takes the confirmation off, because the confirmation was about
// the old value.
//
// THE FIELDS AND THE GROUPS ARE NOT COPIED HERE: src\data\claimSections.js holds which field is in
// which section, src\data\claimAck.js holds the answers. The form object and setter are the page's own,
// so a value typed in a pop-up is the value the validator and the claim see - there is no second form.
//
// ACCESSIBILITY (pass 2 of YCP_PLATFORM_REVIEW_PLAN_2026-10-25): role="dialog", aria-modal, labelled
// by its own heading, focus into the dialog on open and back to the card on close, Tab trapped,
// Escape closes, backdrop closes, scroll locked behind it, and every control a real button with a
// real name. NOT PROVED HERE: how it behaves in a browser - these are DOM behaviours and this
// repository's suites are pure. The browser pass answers that.

import React, { useEffect, useRef, useState } from 'react';
import { ALL_SECTIONS, FIELD_LABELS, sectionState } from '../data/claimSections';
import { ackState, ACK_ACCEPTED, ACK_EDITED } from '../data/claimAck';

const C = {
  card: '#0D1B2A', line: '#2E4A6B', text: '#F4F6F8', dim: '#8AA4BF',
  accent: '#E8A020', ok: '#27AE60', warn: '#FFD27A',
};

const REQUIRED_KEYS = ['claimantName', 'railroad', 'workLocation', 'incidentDate', 'description'];
const LONG_FIELDS = ['description', 'rulesViolated', 'personsInvolved', 'witnesses', 'remedySought'];

function inputKind(key) {
  if (key === 'incidentDate' || key === 'seniorityDate' || key === 'subjectSeniorityDate') return 'date';
  if (key === 'claimantEmail') return 'email';
  if (key === 'claimantPhone') return 'tel';
  return 'text';
}

function ReadRow({ fieldKey, value, derived }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(46,74,107,0.5)' }}>
      <div style={{ flex: '0 0 40%', fontSize: 11.5, color: C.dim }}>
        {FIELD_LABELS[fieldKey] || fieldKey}
        {derived ? <span style={{ color: C.dim }}> (worked out for you)</span> : null}
      </div>
      <div style={{ flex: 1, fontSize: 13, color: value ? C.text : C.warn, wordBreak: 'break-word' }}>
        {value || (REQUIRED_KEYS.indexOf(fieldKey) !== -1 ? 'Required - still empty' : 'Not given')}
      </div>
    </div>
  );
}

function EditRow({ sectionKey, fieldKey, value, onChange, derived }) {
  const id = 'sect-' + sectionKey + '-' + fieldKey;
  const required = REQUIRED_KEYS.indexOf(fieldKey) !== -1;
  if (derived) return <ReadRow fieldKey={fieldKey} value={value} derived />;
  const long = LONG_FIELDS.indexOf(fieldKey) !== -1;
  return (
    <div style={{ marginBottom: 12 }}>
      <label className="form-label" htmlFor={id}>
        {FIELD_LABELS[fieldKey] || fieldKey}{required ? <span style={{ color: C.accent }}> (required)</span> : null}
      </label>
      {long ? (
        <textarea id={id} className="form-textarea" rows={4} value={value || ''}
          onChange={(e) => onChange(fieldKey, e.target.value)} />
      ) : (
        <input id={id} className="form-input" type={inputKind(fieldKey)} value={value || ''}
          onChange={(e) => onChange(fieldKey, e.target.value)} />
      )}
    </div>
  );
}

function Dialog({ section, state, ack, form, set, onAck, onClose, openerRef }) {
  const boxRef = useRef(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const opener = openerRef && openerRef.current;
    const box = boxRef.current;
    if (box) {
      const f = box.querySelector('button, input, select, textarea');
      (f || box).focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !box) return;
      const f = box.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headingId = 'sect-head-' + section.key;
  const blocked = state.missingRequired.length > 0;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,10,18,0.74)', zIndex: 100,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div ref={boxRef} role="dialog" aria-modal="true" aria-labelledby={headingId} tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 10,
                 width: 'min(600px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: 20 }}>
        <h2 id={headingId} style={{ margin: '0 0 4px', fontSize: 18, color: C.text }}>{section.title}</h2>
        <p style={{ margin: '0 0 12px', fontSize: 12.5, color: C.dim, lineHeight: 1.5 }}>{section.why}</p>

        {section.fields.length === 0 ? (
          <div style={{ fontSize: 13, color: C.text, marginBottom: 14 }}>
            There is nothing to read here - attachments are added on the form itself. Confirm that you have
            dealt with your exhibits, or say you have none.
          </div>
        ) : editing ? (
          section.fields.map((k) => (
            <EditRow key={k} sectionKey={section.key} fieldKey={k} value={form ? form[k] : ''}
              onChange={set} derived={section.derived.indexOf(k) !== -1} />
          ))
        ) : (
          <div style={{ marginBottom: 14 }}>
            {section.fields.map((k) => (
              <ReadRow key={k} fieldKey={k} value={form ? form[k] : ''} derived={section.derived.indexOf(k) !== -1} />
            ))}
          </div>
        )}

        <div style={{ fontSize: 12, color: ack.answered ? C.ok : C.warn, margin: '10px 0' }}>{ack.words}</div>
        {blocked && (
          <div style={{ fontSize: 12, color: C.accent, marginBottom: 10 }}>
            {state.summary} &mdash; fill that in before you confirm this section.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
          {!editing && section.fields.filter((k) => section.derived.indexOf(k) === -1).length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
              Edit this section
            </button>
          )}
          {editing && (
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
              Stop editing
            </button>
          )}
          <button type="button" className="btn btn-primary" disabled={blocked}
            onClick={() => { onAck(section.key, editing ? ACK_EDITED : ACK_ACCEPTED); setEditing(false); onClose(); }}>
            {editing ? 'Save and confirm' : 'Correct — no change'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close without confirming
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SectionDialogs({ form, set, onAck, openKey, onOpen, onClose, summary }) {
  const refs = useRef({});
  const open = ALL_SECTIONS.find((s) => s.key === openKey) || null;

  return (
    <div style={{ marginBottom: 16 }}>
      {summary ? (
        <div style={{ fontSize: 13, color: C.text, marginBottom: 10, lineHeight: 1.5 }}>{summary}</div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {ALL_SECTIONS.map((s) => {
          const st = sectionState(form, s.key);
          const ack = ackState(form, s.key);
          if (!refs.current[s.key]) refs.current[s.key] = React.createRef();
          const colour = ack.answered ? C.ok : (st.missingRequired.length ? C.accent : C.warn);
          return (
            <button key={s.key} type="button" ref={refs.current[s.key]} onClick={() => onOpen(s.key)}
              aria-label={s.title + ' - ' + (ack.answered ? 'confirmed' : 'not confirmed') + '. ' + st.summary}
              style={{ flex: '1 1 230px', textAlign: 'left', cursor: 'pointer', font: 'inherit',
                       background: C.card, border: '1px solid ' + colour, borderRadius: 8, padding: '12px 14px' }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.title}</span>
                <span style={{ fontSize: 16, color: colour }} aria-hidden="true">{ack.answered ? '✓' : '○'}</span>
              </span>
              <span style={{ display: 'block', fontSize: 12, color: colour, marginTop: 4 }}>{st.summary}</span>
              <span style={{ display: 'block', fontSize: 11, color: C.dim, marginTop: 6 }}>
                {ack.answered ? ack.words : 'Open it and confirm →'}
              </span>
            </button>
          );
        })}
      </div>
      {open && (
        <Dialog section={open} state={sectionState(form, open.key)} ack={ackState(form, open.key)}
          form={form} set={set} onAck={onAck} onClose={onClose} openerRef={refs.current[open.key]} />
      )}
    </div>
  );
}
